/**
 * Command Palette Commands Definition
 * Per App-Shell.md Section 7.5 and Design-System.md Section 7.5
 */

import { Permissions } from '@/lib/permissions'

export type CommandCategory = 'navigation' | 'actions' | 'recent'

export interface Command {
  id: string
  label: string
  icon?: string // Lucide icon name
  shortcut?: string
  category: CommandCategory
  keywords?: string[] // For search
  action: 'navigate' | 'function'
  href?: string // For navigation commands
  handler?: () => void // For function commands
  /** Shown only to someone holding this permission — the one the destination's menu entry asks for. */
  permission?: string
}

/** The commands this person may see: those with no permission, or one they hold. */
export function commandsFor(commands: Command[], permissions: readonly string[] | undefined): Command[] {
  return commands.filter((c) => !c.permission || !!permissions?.includes(c.permission))
}

/**
 * Navigation commands
 * Per App-Shell.md Command Palette section
 */
export const navigationCommands: Command[] = [
  {
    id: 'nav-dashboard',
    label: 'Go to Dashboard',
    icon: 'LayoutDashboard',
    shortcut: '⌘1',
    category: 'navigation',
    keywords: ['home', 'main', 'overview'],
    action: 'navigate',
    href: '/',
  },
  {
    id: 'nav-quotations',
    label: 'Go to Quotations',
    icon: 'FileText',
    shortcut: '⌘2',
    category: 'navigation',
    keywords: ['quotes', 'rfq', 'pricing'],
    action: 'navigate',
    href: '/quotations',
  },
  {
    id: 'nav-job-orders',
    label: 'Go to Job Orders',
    icon: 'Package',
    shortcut: '⌘3',
    category: 'navigation',
    keywords: ['jobs', 'orders', 'shipments'],
    action: 'navigate',
    href: '/job-orders',
  },
  {
    id: 'nav-customers',
    label: 'Go to Customers',
    icon: 'Users',
    category: 'navigation',
    keywords: ['clients', 'accounts'],
    action: 'navigate',
    href: '/customers',
    // The customer list (Infinity, Oct 2026: not for the pricing and operations teams).
    permission: Permissions.CUSTOMER_LIST_VIEW,
  },
  {
    id: 'nav-settings',
    label: 'Go to Settings',
    icon: 'Settings',
    shortcut: '⌘,',
    category: 'navigation',
    keywords: ['preferences', 'config', 'configuration'],
    action: 'navigate',
    href: '/settings',
  },
  // Finance Module Navigation
  {
    id: 'nav-finance-dashboard',
    label: 'Go to Finance Dashboard',
    icon: 'LayoutDashboard',
    shortcut: 'G D',
    category: 'navigation',
    keywords: ['finance', 'accounting', 'cash', 'balance'],
    action: 'navigate',
    href: '/finance/dashboard',
  },
  {
    id: 'nav-finance-transactions',
    label: 'Go to Transactions',
    icon: 'FileText',
    shortcut: 'G T',
    category: 'navigation',
    keywords: ['finance', 'vouchers', 'payments', 'receipts', 'journals', 'invoices'],
    action: 'navigate',
    href: '/finance/transactions',
  },
  {
    id: 'nav-finance-allocations',
    label: 'Go to Allocations',
    icon: 'FileText',
    shortcut: 'G A',
    category: 'navigation',
    keywords: ['finance', 'allocations', 'matching', 'reconcile'],
    action: 'navigate',
    href: '/finance/allocations',
  },
  {
    id: 'nav-finance-receivables',
    label: 'Go to Receivables',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'receivables', 'ar', 'outstanding', 'collection'],
    action: 'navigate',
    href: '/finance/receivables',
  },
  {
    id: 'nav-finance-payables',
    label: 'Go to Payables',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'payables', 'ap', 'bills', 'vendor'],
    action: 'navigate',
    href: '/finance/payables',
  },
  {
    id: 'nav-finance-cheques',
    label: 'Go to Cheque Register',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'cheques', 'checks', 'bank'],
    action: 'navigate',
    href: '/finance/cheques',
  },
  {
    id: 'nav-finance-accounts',
    label: 'Go to Chart of Accounts',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'ledger', 'accounts', 'coa', 'chart'],
    action: 'navigate',
    href: '/finance/masters/accounts',
  },
  {
    id: 'nav-finance-parties',
    label: 'Go to Subledger Accounts',
    icon: 'Users',
    category: 'navigation',
    keywords: ['finance', 'customers', 'vendors', 'subledger', 'parties'],
    action: 'navigate',
    href: '/finance/masters/parties',
  },
  {
    id: 'nav-finance-reports',
    label: 'Go to Finance Reports',
    icon: 'FileText',
    shortcut: 'G R',
    category: 'navigation',
    keywords: ['finance', 'reports', 'trial balance', 'balance sheet', 'pl', 'profit', 'loss'],
    action: 'navigate',
    href: '/finance/reports',
  },
  {
    id: 'nav-finance-settings',
    label: 'Go to Finance Settings',
    icon: 'Settings',
    category: 'navigation',
    keywords: ['finance', 'settings', 'config', 'voucher types', 'mapping', 'periods', 'audit'],
    action: 'navigate',
    href: '/finance/settings',
  },
  {
    id: 'nav-finance-voucher-types',
    label: 'Go to Voucher Types',
    icon: 'Settings',
    category: 'navigation',
    keywords: ['finance', 'voucher', 'types', 'numbering', 'prefix', 'config'],
    action: 'navigate',
    href: '/finance/settings/voucher-types',
  },
  {
    id: 'nav-finance-account-mapping',
    label: 'Go to Account Mapping',
    icon: 'Settings',
    category: 'navigation',
    keywords: ['finance', 'account', 'mapping', 'ledger', 'receivable', 'payable', 'vat'],
    action: 'navigate',
    href: '/finance/settings/account-mapping',
  },
  {
    id: 'nav-finance-fiscal-periods',
    label: 'Go to Fiscal Periods',
    icon: 'Settings',
    category: 'navigation',
    keywords: ['finance', 'fiscal', 'periods', 'year', 'close', 'lock'],
    action: 'navigate',
    href: '/finance/settings/periods',
  },
  {
    id: 'nav-finance-audit-trail',
    label: 'Go to Audit Trail',
    icon: 'Settings',
    category: 'navigation',
    keywords: ['finance', 'audit', 'trail', 'log', 'history', 'changes'],
    action: 'navigate',
    href: '/finance/settings/audit-log',
  },
  // Finance Home / Start Page
  {
    id: 'nav-finance-home',
    label: 'Go to Finance Home',
    icon: 'LayoutDashboard',
    category: 'navigation',
    keywords: ['finance', 'home', 'start', 'main', 'landing'],
    action: 'navigate',
    href: '/finance',
  },
  // Individual Report Pages
  {
    id: 'nav-finance-trial-balance',
    label: 'Go to Trial Balance Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'trial', 'balance', 'tb', 'debit', 'credit'],
    action: 'navigate',
    href: '/finance/reports/trial-balance',
  },
  {
    id: 'nav-finance-balance-sheet',
    label: 'Go to Balance Sheet Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'balance', 'sheet', 'bs', 'assets', 'liabilities', 'equity'],
    action: 'navigate',
    href: '/finance/reports/balance-sheet',
  },
  {
    id: 'nav-finance-profit-loss',
    label: 'Go to Profit & Loss Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'profit', 'loss', 'pl', 'income', 'expense', 'statement'],
    action: 'navigate',
    href: '/finance/reports/profit-loss',
  },
  {
    id: 'nav-finance-general-ledger',
    label: 'Go to General Ledger Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'general', 'ledger', 'gl', 'account', 'transactions'],
    action: 'navigate',
    href: '/finance/reports/general-ledger',
  },
  {
    id: 'nav-finance-receivables-aging',
    label: 'Go to Receivables Aging Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'receivables', 'aging', 'ar', 'outstanding', 'overdue', 'customer'],
    action: 'navigate',
    href: '/finance/reports/aging/receivables',
  },
  {
    id: 'nav-finance-payables-aging',
    label: 'Go to Payables Aging Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'payables', 'aging', 'ap', 'outstanding', 'overdue', 'vendor'],
    action: 'navigate',
    href: '/finance/reports/aging/payables',
  },
  {
    id: 'nav-finance-customer-statement',
    label: 'Go to Customer Statement',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'customer', 'statement', 'account', 'balance', 'history'],
    action: 'navigate',
    href: '/finance/reports/statement/customer',
  },
  {
    id: 'nav-finance-vendor-statement',
    label: 'Go to Vendor Statement',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'vendor', 'statement', 'account', 'balance', 'supplier', 'history'],
    action: 'navigate',
    href: '/finance/reports/statement/vendor',
  },
  {
    id: 'nav-finance-cash-position',
    label: 'Go to Cash Position Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'cash', 'position', 'bank', 'balance', 'flow'],
    action: 'navigate',
    href: '/finance/reports/cash-position',
  },
  {
    id: 'nav-finance-vat-report',
    label: 'Go to VAT Report',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'report', 'vat', 'tax', 'input', 'output', 'return'],
    action: 'navigate',
    href: '/finance/reports/vat',
  },
  {
    id: 'nav-finance-guide',
    label: 'Go to Finance User Guide',
    icon: 'FileText',
    category: 'navigation',
    keywords: ['finance', 'guide', 'help', 'documentation', 'how to', 'tutorial'],
    action: 'navigate',
    href: '/finance/guide',
  },
]

/**
 * Action commands
 */
export const actionCommands: Command[] = [
  {
    id: 'action-new-quote',
    label: 'Create New Quotation',
    icon: 'Plus',
    shortcut: '⌘N',
    category: 'actions',
    keywords: ['new', 'add', 'create', 'quote'],
    action: 'function',
    // handler will be set dynamically
  },
  {
    id: 'action-new-customer',
    label: 'Create New Customer',
    icon: 'UserPlus',
    category: 'actions',
    keywords: ['new', 'add', 'create', 'customer', 'client'],
    action: 'function',
    // Will open CustomerFormModal when implemented in Block 4
  },
  {
    id: 'action-focus-mode',
    label: 'Enter Focus Mode',
    icon: 'Play',
    shortcut: '⌘⇧F',
    category: 'actions',
    keywords: ['focus', 'tasks', 'queue', 'productivity'],
    action: 'function',
    // handler will be set dynamically
  },
  // Finance quick actions (Opt/Alt key)
  {
    id: 'action-new-payment',
    label: 'New Payment Voucher',
    icon: 'Plus',
    shortcut: 'Opt+P',
    category: 'actions',
    keywords: ['finance', 'payment', 'pay', 'vendor', 'bill'],
    action: 'navigate',
    href: '/finance/transactions?action=new-payment',
  },
  {
    id: 'action-new-receipt',
    label: 'New Receipt Voucher',
    icon: 'Plus',
    shortcut: 'Opt+R',
    category: 'actions',
    keywords: ['finance', 'receipt', 'receive', 'customer', 'invoice'],
    action: 'navigate',
    href: '/finance/transactions?action=new-receipt',
  },
  {
    id: 'action-new-journal',
    label: 'New Journal Entry',
    icon: 'Plus',
    shortcut: 'Opt+J',
    category: 'actions',
    keywords: ['finance', 'journal', 'entry', 'adjustment'],
    action: 'navigate',
    href: '/finance/transactions?action=new-journal',
  },
  {
    id: 'action-new-credit-note',
    label: 'New Credit Note',
    icon: 'Plus',
    category: 'actions',
    keywords: ['finance', 'credit', 'note', 'cn', 'refund', 'return', 'sales return'],
    action: 'navigate',
    href: '/finance/transactions?action=new-credit-note',
  },
  {
    id: 'action-new-debit-note',
    label: 'New Debit Note',
    icon: 'Plus',
    category: 'actions',
    keywords: ['finance', 'debit', 'note', 'dn', 'purchase return', 'vendor return'],
    action: 'navigate',
    href: '/finance/transactions?action=new-debit-note',
  },
  {
    id: 'action-new-sales-invoice',
    label: 'New Sales Invoice',
    icon: 'Plus',
    category: 'actions',
    keywords: ['finance', 'sales', 'invoice', 'si', 'bill', 'customer invoice'],
    action: 'navigate',
    href: '/finance/transactions?action=new-sales-invoice',
  },
  {
    id: 'action-new-purchase-invoice',
    label: 'New Purchase Invoice',
    icon: 'Plus',
    category: 'actions',
    keywords: ['finance', 'purchase', 'invoice', 'pi', 'vendor', 'bill', 'expense'],
    action: 'navigate',
    href: '/finance/transactions?action=new-purchase-invoice',
  },
]

/**
 * Get all static commands
 */
export function getAllCommands(): Command[] {
  return [...navigationCommands, ...actionCommands]
}
